import { Select as SelectPrimitive } from '@base-ui/react/select'
import { cva, VariantProps } from 'class-variance-authority'
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, X } from 'lucide-react'
import * as React from 'react'
import { isValidElement, ReactNode } from 'react'

import { cn } from '@eigtech/ui/lib/utils'

// Create a Context for `indicatorPosition` and `indicator` control
const SelectContext = React.createContext<{
  indicatorPosition: 'left' | 'right'
  indicatorVisibility: boolean
  indicator: ReactNode
  icon: ReactNode
}>({ indicatorPosition: 'left', indicator: null, indicatorVisibility: true, icon: null })

// Root Component
function Select<Value, Multiple extends boolean | undefined = false>({
  indicatorPosition = 'left',
  indicatorVisibility = true,
  indicator,
  icon,
  ...props
}: {
  indicatorPosition?: 'left' | 'right'
  indicatorVisibility?: boolean
  indicator?: ReactNode
  icon?: ReactNode
} & React.ComponentProps<typeof SelectPrimitive.Root<Value, Multiple>>) {
  return (
    <SelectContext.Provider value={{ indicatorPosition, indicatorVisibility, indicator, icon }}>
      <SelectPrimitive.Root data-slot="select" {...props} />
    </SelectContext.Provider>
  )
}

function SelectGroup({ ...props }: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />
}

function SelectPortal({ ...props }: React.ComponentProps<typeof SelectPrimitive.Portal>) {
  return <SelectPrimitive.Portal data-slot="select-portal" {...props} />
}

function SelectPositioner({ ...props }: React.ComponentProps<typeof SelectPrimitive.Positioner>) {
  return <SelectPrimitive.Positioner data-slot="select-positioner" {...props} />
}

function SelectValue({
  placeholder,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value> & {
  placeholder?: string
}) {
  if (!placeholder) {
    return <SelectPrimitive.Value data-slot="select-value" {...props} />
  }

  return (
    <SelectPrimitive.Value
      className="text-sm"
      render={(_, { value }) => {
        if (value) {
          return <SelectPrimitive.Value data-slot="select-value" {...props} />
        }

        // Placeholder
        return (
          <span className="text-muted-foreground" data-slot="select-value">
            {placeholder}
          </span>
        )
      }}
      {...props}
    />
  )
}

// Clear - A button to clear the input value
function SelectClear({ className, children, onClick, ...props }: React.ComponentProps<'button'>) {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    onClick?.(event)
  }

  return (
    <button
      className={cn(
        `data-disabled:pointer-events-none absolute end-2 top-1/2 -translate-y-1/2 cursor-pointer rounded-sm opacity-60 transition-opacity hover:opacity-100 focus:outline-none focus:ring-0 focus:ring-offset-0 disabled:pointer-events-none`,
        className
      )}
      data-slot="select-clear"
      onClick={handleClick}
      {...props}
    >
      {children ? children : <X />}
    </button>
  )
}

// Define size variants for SelectTrigger
const selectTriggerVariants = cva(
  `focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/50 aria-invalid:border-destructive [&_svg:not([class*='text-'])]:text-muted-foreground shadow-xs data-disabled:pointer-events-none data-disabled:opacity-60 group relative flex w-fit select-none items-center justify-between gap-2 whitespace-nowrap rounded-md border bg-transparent outline-none transition-[color,box-shadow,border-color] focus-visible:ring-[3px] *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0`,
  {
    variants: {
      size: {
        xs: `**:data-[slot=select-icon]:-me-0.75 **:data-[slot=select-clear]:end-6 **:data-[slot=select-icon]:size-3.5 h-7 gap-1 rounded-md px-2 text-xs [&_[data-slot=select-clear]>svg]:size-3`,
        sm: `**:data-[slot=select-icon]:-me-0.75 **:data-[slot=select-clear]:end-6 **:data-[slot=select-icon]:size-3.5 h-8 gap-1 rounded-md px-2.5 text-xs [&_[data-slot=select-clear]>svg]:size-3`,
        md: `**:data-[slot=select-clear]:end-7 **:data-[slot=select-icon]:-me-1 **:data-[slot=select-icon]:size-4 h-9 gap-1 rounded-md px-3 text-sm [&_[data-slot=select-clear]>svg]:size-3.5`,
        lg: `**:data-[slot=select-icon]:-me-1.25 **:data-[slot=select-clear]:end-8 **:data-[slot=select-icon]:size-4 h-10 gap-1.5 rounded-md px-4 text-sm [&_[data-slot=select-clear]>svg]:size-3.5`,
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

export interface SelectTriggerProps
  extends
    React.ComponentProps<typeof SelectPrimitive.Trigger>,
    VariantProps<typeof selectTriggerVariants> {}

function SelectTrigger({ className, children, size, ...props }: SelectTriggerProps) {
  const { icon } = React.useContext(SelectContext)

  return (
    <SelectPrimitive.Trigger
      className={cn(selectTriggerVariants({ size }), className)}
      data-slot="select-trigger"
      {...props}
    >
      {children}
      <SelectPrimitive.Icon>
        {icon && isValidElement(icon) ? (
          icon
        ) : (
          <ChevronDownIcon
            className="opacity-60 transition-transform duration-200"
            data-slot="select-icon"
          />
        )}
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}

function SelectContent({
  className,
  children,
  side = 'bottom',
  sideOffset = 2,
  align = 'start',
  alignOffset = 0,
  position = 'popper',
  positionerClassName,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Popup> & {
  sideOffset?: SelectPrimitive.Positioner.Props['sideOffset']
  side?: SelectPrimitive.Positioner.Props['side']
  align?: SelectPrimitive.Positioner.Props['align']
  alignOffset?: SelectPrimitive.Positioner.Props['alignOffset']
  position?: 'popper' | 'item-aligned'
  positionerClassName?: string
}) {
  return (
    <SelectPortal>
      <SelectPositioner
        align={align}
        alignItemWithTrigger={position === 'item-aligned'}
        alignOffset={alignOffset}
        className={cn('z-50', positionerClassName)}
        side={side}
        sideOffset={sideOffset}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Popup
          className={cn(
            `bg-popover text-popover-foreground data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 max-h-(--available-height) min-w-(--anchor-width) origin-(--transform-origin) relative z-50 overflow-y-auto overflow-x-hidden rounded-md border p-1 shadow-md`,
            position === 'item-aligned' && '[&_*[data-slot=select-item]]:min-w-(--anchor-width)',
            className
          )}
          data-slot="select-content"
          {...props}
        >
          {children}
        </SelectPrimitive.Popup>
        <SelectScrollDownButton />
      </SelectPositioner>
    </SelectPortal>
  )
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item> & { id?: string }) {
  const { indicatorPosition, indicatorVisibility, indicator } = React.useContext(SelectContext)

  return (
    <SelectPrimitive.Item
      className={cn(
        `data-highlighted:bg-accent data-highlighted:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground outline-hidden *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2 data-disabled:pointer-events-none data-disabled:opacity-50 relative flex w-full cursor-default cursor-pointer select-none items-center rounded-sm py-1.5 text-sm [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0`,
        indicatorPosition === 'left' ? 'pe-2 ps-7' : 'pe-7 ps-2',
        className
      )}
      data-slot="select-item"
      {...props}
    >
      {indicatorVisibility &&
        (indicator && isValidElement(indicator) ? (
          indicator
        ) : (
          <span
            className={cn(
              'absolute flex h-3.5 w-3.5 items-center justify-center',
              indicatorPosition === 'left' ? 'start-2' : 'end-2'
            )}
          >
            <SelectPrimitive.ItemIndicator data-slot="select-item-indicator">
              <CheckIcon className="text-primary h-4 w-4" />
            </SelectPrimitive.ItemIndicator>
          </span>
        ))}
      <SelectPrimitive.ItemText data-slot="select-item-text">{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.GroupLabel>) {
  const { indicatorPosition } = React.useContext(SelectContext)

  return (
    <SelectPrimitive.GroupLabel
      className={cn(
        'text-muted-foreground py-1.5 text-xs font-medium',
        indicatorPosition === 'left' ? 'pe-2 ps-7' : 'pe-7 ps-2',
        className
      )}
      data-slot="select-label"
      {...props}
    />
  )
}

function SelectIndicator({
  children,
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ItemIndicator>) {
  const { indicatorPosition } = React.useContext(SelectContext)

  return (
    <SelectPrimitive.ItemIndicator
      className={cn(
        'absolute top-1/2 flex -translate-y-1/2 items-center justify-center',
        indicatorPosition === 'left' ? 'start-2' : 'end-2',
        className
      )}
      data-slot="select-indicator"
      {...props}
    >
      {children}
    </SelectPrimitive.ItemIndicator>
  )
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
      data-slot="select-separator"
      {...props}
    />
  )
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
  return (
    <SelectPrimitive.ScrollUpArrow
      className={cn(
        'bg-popover fixed left-0 right-0 top-0 z-10 flex w-full cursor-default items-center justify-center rounded-t-md py-1',
        className
      )}
      data-slot="select-scroll-up-button"
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpArrow>
  )
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
  return (
    <SelectPrimitive.ScrollDownArrow
      className={cn(
        'bg-popover fixed bottom-0 left-0 right-0 z-10 flex w-full cursor-default items-center justify-center rounded-b-md py-1',
        className
      )}
      data-slot="select-scroll-down-button"
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownArrow>
  )
}

export {
  Select,
  SelectClear,
  SelectContent,
  SelectGroup,
  SelectIndicator,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
